import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ConfirmButton } from "@/components/ConfirmButton";
import { useOperators } from "@/components/mobile/operators";
import { simCount, simOperators } from "@/components/sim/DealerSimCounts";
import { ArrowDownLeft, ArrowUpRight } from "lucide-react";

const OpCard = ({ o, mine, theirs, active, onPick }) => <button type="button" onClick={onPick} aria-pressed={active}
  className={`rounded-lg border p-3 text-left transition-colors ${active ? "border-primary bg-primary/10" : "border-border hover:bg-secondary/50"}`} data-testid={`sim-quick-op-${o.key}`}>
  <p className="text-sm font-semibold">{o.name}</p>
  <p className="text-xs mt-1"><span className="text-muted-foreground">Le tue: </span><strong data-testid={`sim-quick-mine-${o.key}`}>{mine}</strong></p>
  <p className="text-xs"><span className="text-muted-foreground">Del dealer: </span><strong data-testid={`sim-quick-dealer-${o.key}`}>{theirs}</strong></p>
</button>;

export const SimQuickMove = ({ dealer, meId, onClose, onDone }) => {
  const ops = simOperators(useOperators());
  const [mine, setMine] = useState(null);
  const [pick, setPick] = useState("");
  const op = ops.find((o) => o.key === pick) || (ops.length === 1 ? ops[0] : null);
  const [dir, setDir] = useState("give");
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!dealer) return;
    setMine(null); setPick(""); setQty("");
    api.get("/sim-stock/counts").then(({ data }) => setMine(Object.fromEntries((data.items || []).map((i) => [i.key, Number(i.stock) || 0])))).catch(() => setMine({}));
  }, [dealer]);
  const myN = (key) => mine?.[key] ?? 0;
  const max = op ? (dir === "give" ? myN(op.key) : simCount(dealer, op.key)) : 0;
  const n = Number(qty);
  const valid = n >= 1 && n <= max && Number.isInteger(n);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/sim-stock/move", { ...(dir === "give" ? { from_id: meId, to_id: dealer.id } : { from_id: dealer.id, to_id: meId }), qty: n, note: "", operator: op.key });
      toast.success(dir === "give" ? `${n} SIM ${op.name} assegnate a ${dealer.name}` : `${n} SIM ${op.name} riprese da ${dealer.name}`);
      window.dispatchEvent(new Event("sim-changed")); onDone?.(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Spostamento non riuscito"); }
    finally { setBusy(false); }
  };
  return <Dialog open={!!dealer} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid="sim-quick-move-dialog">
      <DialogHeader><DialogTitle>Sposta SIM · {dealer?.name}</DialogTitle></DialogHeader>
      <div className="space-y-2" data-testid="sim-quick-operators">
        <p className="text-sm font-medium">Per quale operatore?</p>
        {mine === null ? <p className="text-sm text-muted-foreground" data-testid="sim-quick-loading">Carico le tue SIM…</p>
          : <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">{ops.map((o) => <OpCard key={o.key} o={o} mine={myN(o.key)} theirs={simCount(dealer, o.key)} active={op?.key === o.key} onPick={() => { setPick(o.key); setQty(""); }} />)}</div>}
      </div>
      {op && mine !== null && <>
        <div className="compact-actions grid grid-cols-2 gap-2">
          <Button variant={dir === "give" ? "default" : "outline"} className="gap-2" onClick={() => { setDir("give"); setQty(""); }} data-testid="sim-quick-give"><ArrowUpRight className="w-4 h-4" />Dai SIM al dealer</Button>
          <Button variant={dir === "take" ? "default" : "outline"} className="gap-2" onClick={() => { setDir("take"); setQty(""); }} data-testid="sim-quick-take"><ArrowDownLeft className="w-4 h-4" />Riprendi SIM</Button>
        </div>
        <p className="text-sm" data-testid="sim-quick-stock">{dir === "give" ? <>Hai <strong>{max}</strong> SIM {op.name} da dare a {dealer?.name}.</> : <>{dealer?.name} ha <strong>{max}</strong> SIM {op.name} da riprendere.</>}</p>
        {max === 0 ? <p className="text-sm text-amber-600 dark:text-amber-400" data-testid="sim-quick-empty">{dir === "give" ? `Non hai SIM ${op.name} disponibili da spostare.` : `Il dealer non ha SIM ${op.name} da riprendere.`}</p> : <>
          <Input type="number" min="1" max={max} placeholder={`Quante SIM? (max ${max})`} value={qty} onChange={(e) => setQty(e.target.value)} data-testid="sim-quick-qty" />
          {qty !== "" && !valid && <p className="text-xs text-destructive" data-testid="sim-quick-qty-error">Inserisci un numero da 1 a {max}.</p>}
          <ConfirmButton disabled={busy || !valid} onConfirm={submit} data-testid="sim-quick-submit" confirmTestId="sim-quick-confirm" title="Confermi lo spostamento?"
            description={dir === "give" ? `Stai dando ${n} SIM ${op.name} solo a ${dealer?.name}: escono dalle tue SIM.` : `Stai riprendendo ${n} SIM ${op.name} da ${dealer?.name}: tornano alle tue SIM.`} confirmText="Conferma">
            {busy ? "Spostamento…" : `Sposta ${valid ? n : ""} SIM ${op.name}`}
          </ConfirmButton>
        </>}
      </>}
    </DialogContent>
  </Dialog>;
};
